/**
 * @module cercaliagl/BaseFeature
 */

import polyline from '@mapbox/polyline';
import { createUid, createCircleGeometry } from './util';
import BaseObject from './BaseObject';
import LonLat from './LonLat';
/**
 * @typedef {Object} BaseFeatureOptions
 * @property {string} [id] Id
 * @property {function(this:Marker): void} [onClick] Function to execute on marker click.
 * @property {function(this:Marker): void} [onDoubleClick] Function to execute on marker double click.
 * @property {function(this:Marker): void} [onMouseOut] Function to execute on Mouse Out.
 * @property {function(this:Marker): void} [onMouseOver] Function to execute on Mouse Over.
 * @property {function(this:Marker): void} [onMouseMove] Function to execute on Mouse Move.
 * @property {function(this:Marker): void} [onRightClick] Function to execute on marker right click.
 */

/**
 * @constant
 * @type {Map.<string, string>}
 */
const encodeMap = new Map([
  ['0', 'B'],
  ['1', 'C'],
  ['2', 'D'],
  ['3', 'T'],
  ['4', 'U'],
  ['5', 'V'],
  ['6', 'G'],
  ['7', 'H'],
  ['8', 'I'],
  ['9', '3'],
  ['A', '4'], ['a', 'i'],
  ['B', '5'], ['b', 'j'],
  ['C', 'w'], ['c', 'k'],
  ['D', 'x'], ['d', 'l'],
  ['E', 'y'], ['e', '6'],
  ['F', 'P'], ['f', '7'],
  ['G', 'Q'], ['g', '8'],
  ['H', 'R'], ['h', '9'],
  ['I', 'S'], ['i', 'A'],
  ['J', 'W'], ['j', 'a'],
  ['K', 'X'], ['k', 'b'],
  ['L', 'Y'], ['l', 'c'],
  ['M', 'Z'], ['m', 'd'],
  ['N', 'r'], ['n', 'J'],
  ['O', 's'], ['o', 'K'],
  ['P', 't'], ['p', 'L'],
  ['Q', 'u'], ['q', 'M'],
  ['R', 'v'], ['r', 'e'],
  ['S', 'm'], ['s', 'f'],
  ['T', 'n'], ['t', 'g'],
  ['U', 'o'], ['u', 'h'],
  ['V', 'p'], ['v', 'z'],
  ['W', 'q'], ['w', 'N'],
  ['X', '0'], ['x', 'O'],
  ['Y', '1'], ['y', 'E'],
  ['Z', '2'], ['z', 'F'],
]);

function translateEncodeDataToGooglePolyline(encodeData) {
  let encodedString = '';
  for (let i = 0; i < encodeData.length; i += 1) {
    const currentLetter = encodeMap.get(encodeData.charAt(i));
    encodedString += currentLetter ?? encodeData.charAt(i);
  }
  return encodedString;
}

/**
 * Encodes a google polyline
 * @param {string} googlePolyline Google polyline
 * @returns {string} Encoded polyline
 */
function translateGooglePolylineToEncodedData(googlePolyline) {
  const reverseEncodeMap = new Map(
    Array.from(encodeMap, ([key, value]) => [value, key]),
  );

  return googlePolyline
    .split('')
    .map((char) => reverseEncodeMap.get(char) ?? char)
    .join('');
}

function decodeCoords_(encodeData, returnString = true) {
  const coords = polyline.decode(encodeData);
  if (!returnString) {
    return coords;
  }
  let wkt = '(';
  const lastPosition = coords.length - 1;
  for (let i = 0; i < lastPosition; i += 1) {
    wkt += `${coords[i][1]} ${coords[i][0]}, `;
  }
  wkt += `${coords[lastPosition][1]} ${coords[lastPosition][0]})`;
  return wkt;
}

function encodedDataToCircleCoords_(encodeData, extrapass = false) {
  const encodedString = extrapass ? translateEncodeDataToGooglePolyline(encodeData) : encodeData;
  return decodeCoords_(encodedString, false);
}

/**
 * @classdesc
 * Abstract class for internal purposes. Don't use it.
 */
class BaseFeature extends BaseObject {
  /**
   * @param {string} [id]
   */
  constructor(options = {}) {
    super();

    /**
     * @protected
     * @type {string}
     */
    this.id_ = options.id ? options.id : createUid();

    /**
     * @protected
     * @type {import("./layer/Base").default}
     */
    this.layer_ = null;

    /**
     * @type {function(this:Marker) : void|null}
     */
    this.onClick = options.onClick ? options.onClick : null;

    /**
     * @type {Function|null}
     */
    this.onDoubleClick = options.onDoubleClick ? options.onDoubleClick : null;

    /**
     * @type {Function|null}
     */
    this.onRightClick = options.onRightClick ? options.onRightClick : null;

    /**
     * @type {Function|null}
     */
    this.onMouseOver = options.onMouseOver ? options.onMouseOver : null;

    /**
     * @type {Function|null}
     */
    this.onMouseMove = options.onMouseMove ? options.onMouseMove : null;

    /**
     * @type {Function|null}
     */
    this.onMouseOut = options.onMouseOut ? options.onMouseOut : null;

    /**
     * @protected
     * @type {GeoJSON}
     */
    this.geojson_ = null;

    /**
     * @protected
     * @type {import("./Map").default}
     */
    this.map_ = null;
  }

  /**
   * @return {String} Id
   * @api
   */
  getId() {
    return this.id_;
  }

  /**
   * @param {import("./layer/Base").default} layer Layer
   */
  setLayer(layer) {
    this.layer_ = layer;
  }

  /**
   * @return {import("./layer/Base").default} Layer
   * @api
   */
  getLayer() {
    return this.layer_;
  }

  /**
   * @return {GeoJSON} GeoJSON
   * @api
   */
  getGeoJSON() {
    return this.geojson_;
  }

  /**
   * @return {string} GeoJSON
   * @api
   */
  getGeometryType() {
    return this.geojson_.geometry ? this.geojson_.geometry.type : null;
  }

  /**
   * @return {import("./Map").default} Map
   * @api
   */
  getMap() {
    return this.map_;
  }

  /**
   * @param {import('./Map').default} map Map
   * @api
   */
  setMap(map) {
    this.map_ = map;
  }

  /**
   * @private
  */
  encodedDataToLinestringWKT_(encodeData, extrapass = false) {
    const encodedString = extrapass ? translateEncodeDataToGooglePolyline(encodeData) : encodeData;
    const coords = decodeCoords_(encodedString, this.encodedPrecision_);
    const wkt = `LINESTRING ${coords}`;
    return wkt;
  }

  /**
   * @private
  */
  // eslint-disable-next-line class-methods-use-this
  encodedDataToPointPosition_(encodeData, extrapass = false) {
    const encodedString = extrapass ? translateEncodeDataToGooglePolyline(encodeData) : encodeData;
    const coords = decodeCoords_(encodedString, false);
    return new LonLat(coords[0][1], coords[0][0]);
  }

  /**
   * @private
   * @param {string} encodeData
   * @param {boolean} extrapass
  */
  encodedDataToPolygonWKT_(encodeData, extrapass = false) {
    const coords = this.decodeDataToPolygonWKT_(encodeData, extrapass);
    const wkt = `POLYGON (${coords})`;
    return wkt;
  }

  /**
   * @private
   * @param {Array.<string>} encodeData
   * @param {boolean} extrapass
  */
  encodedDataToMultiPolygonWKT_(encodeData, extrapass = false) {
    let wkt = 'MULTIPOLYGON (';
    encodeData.forEach((encodedString, index) => {
      wkt += `(${this.decodeDataToPolygonWKT_(encodedString, extrapass)})${index < encodeData.length - 1 ? ',' : ')'}`;
    });
    return wkt;
  }

  /**
   * @private
   * @param {string} encodeData
   * @param {boolean} extrapass
  */
  decodeDataToPolygonWKT_(encodeData, extrapass = false) {
    let wkt = '';
    const encodedDataSplitted = encodeData.split('-');
    encodedDataSplitted.forEach((encodedPolygon, index) => {
      const encodedString = extrapass ? translateEncodeDataToGooglePolyline(encodedPolygon) : encodedPolygon;
      const coords = decodeCoords_(encodedString, this.encodedPrecision_);
      wkt += `${coords}${index < encodedDataSplitted.length - 1 ? ',' : ''}`;
    });
    return wkt;
  }

  /**
   * @private
  */
  static getCircleGeometryFromEncodedData_(encodeCenter, radius, vertices = 64) {
    const coords = encodedDataToCircleCoords_(encodeCenter, true);
    const center = new LonLat(coords[0][1], coords[0][0]);
    return createCircleGeometry(center, radius, vertices);
  }

  /**
 * Create circle geometry given a `center`, a radius and optionally number of `vertices`.
 * @api
 * @param {string} encodeCenter Center encoded on google polyline format.
 * @param {number} radius Radius in meters
 * @param {number} [vertices] Number of vertices. Default `64`.
 * @return {import("./util").GeometryGeoJSON} geometry
 */
  static googleEncodedCircleToGeometry(encodeCenter, radius, vertices = 64) {
    const coords = encodedDataToCircleCoords_(encodeCenter);
    const center = new LonLat(coords[0][1], coords[0][0]);
    return createCircleGeometry(center, radius, vertices);
  }
}

export default BaseFeature;
